import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { isValidPhoneNumber } from "react-phone-number-input";

const COUNTRY_CODES = [
  { code: "+254", country: "KE", flag: "🇰🇪" },
  { code: "+255", country: "TZ", flag: "🇹🇿" },
  { code: "+256", country: "UG", flag: "🇺🇬" },
  { code: "+1", country: "US", flag: "🇺🇸" },
  { code: "+44", country: "GB", flag: "🇬🇧" },
];

/**
 * Custom phone input with country code dropdown.
 * Uses react-phone-number-input for validation only.
 *
 * Props:
 *   value - the phone number (without country code)
 *   onChange(value) - called when phone number changes
 *   countryCode - current country code (default: "+254")
 *   onCountryCodeChange(code) - called when country code changes
 *   onValidChange(isValid) - called when validity changes
 *   placeholder - input placeholder
 *   touched - whether field has been touched (for styling)
 *   className - additional container classes
 */
export const PhoneInputEmpire = ({
  value = "",
  onChange,
  countryCode = "+254",
  onCountryCodeChange,
  onValidChange,
  placeholder = "712 345 678",
  touched = false,
  className = "",
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const normalizedPhone = value.replace(/^0/, "");
  const fullPhoneNumber = normalizedPhone ? `${countryCode}${normalizedPhone}` : "";

  const isKenyanCode = countryCode === "+254";
  const kenyanFirstDigitValid = !isKenyanCode || !normalizedPhone || /^[17]/.test(normalizedPhone);
  const isValid = fullPhoneNumber && isValidPhoneNumber(fullPhoneNumber) && kenyanFirstDigitValid;

  useEffect(() => {
    onValidChange?.(isValid);
  }, [isValid, onValidChange]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    onChange(raw);
  };

  const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0];

  const containerClass = `
    flex items-center bg-neutral-900 border rounded-xl h-12 transition-all duration-200
    ${touched && value && !isValid ? "border-red-500 ring-2 ring-red-500/20" : ""}
    ${touched && isValid ? "border-green-500 ring-2 ring-green-500/20" : ""}
    ${!touched || !value ? "border-neutral-800 focus-within:border-empire-gold focus-within:ring-2 focus-within:ring-empire-gold/20" : ""}
    ${className}
  `.trim();

  return (
    <div className={containerClass}>
      <div ref={dropdownRef} className="relative h-full">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="flex items-center gap-2 h-full px-4 border-r border-neutral-700 hover:bg-neutral-800/50 transition-colors rounded-l-xl"
        >
          <span className="text-base">{selectedCountry.flag}</span>
          <span className="text-neutral-400 text-sm font-medium">{countryCode}</span>
          <ChevronDown className="w-3 h-3 text-neutral-500" />
        </button>

        {showDropdown && (
          <div className="absolute left-0 top-full mt-1 bg-neutral-900 border border-neutral-700 rounded-lg shadow-xl z-50 min-w-[160px]">
            {COUNTRY_CODES.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => {
                  onCountryCodeChange?.(c.code);
                  setShowDropdown(false);
                }}
                className={`flex items-center gap-2 w-full px-3 py-2.5 text-left hover:bg-neutral-800 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                  countryCode === c.code ? "bg-neutral-800 text-empire-gold" : "text-neutral-300"
                }`}
              >
                <span className="text-base">{c.flag}</span>
                <span className="text-sm font-medium">{c.code}</span>
                <span className="text-xs text-neutral-500 ml-auto">{c.country}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <input
        type="tel"
        inputMode="numeric"
        value={value}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        className="flex-1 bg-transparent h-full px-4 text-white placeholder-neutral-500 outline-none border-0 text-base tracking-wide"
      />

      {touched && isValid && (
        <div className="pr-3">
          <svg className="w-5 h-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      )}
    </div>
  );
};

export const getFullPhoneNumber = (countryCode, phone) => {
  const normalized = phone.replace(/^0/, "");
  return normalized ? `${countryCode}${normalized}` : "";
};

export { isValidPhoneNumber };
